import { getStoredToken } from './authApi';
import { send } from './serverClient';
import { getServerUrl } from './updateChannel';

// Every request resolves the URL at call time so a channel toggle
// (stable ↔ beta) applies without a module reload.
const baseUrl = () => getServerUrl();

async function authHeaders() {
  const token = await getStoredToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

export async function searchPlayers(query) {
  const res = await fetch(`${baseUrl()}/friends/search?q=${encodeURIComponent(query)}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    console.warn(`Friends search failed: ${res.status} ${res.statusText}`);
    return [];
  }
  return res.json();
}

export async function getFriendList() {
  // Cache-bust the URL. Friend metadata (avatars, names, ranks) needs to
  // reflect the current server state on every poll. Without this, CEF or any
  // intermediate cache could serve a stale snapshot indefinitely.
  const res = await fetch(`${baseUrl()}/friends?_t=${Date.now()}`, {
    headers: { ...(await authHeaders()), 'Cache-Control': 'no-cache' },
  });
  if (!res.ok) return { friends: [], pendingRequests: [], pendingCount: 0, pendingInvites: [], pendingSpectate: [], acceptedNotifications: [] };
  return res.json();
}

export async function sendFriendRequest(targetId) {
  const res = await fetch(`${baseUrl()}/friends/request`, {
    method: 'POST', headers: await authHeaders(),
    body: JSON.stringify({ targetId }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to send request'); }
  return res.json();
}

export async function acceptFriendRequest(senderId) {
  const res = await fetch(`${baseUrl()}/friends/accept`, {
    method: 'POST', headers: await authHeaders(),
    body: JSON.stringify({ senderId }),
  });
  if (!res.ok) throw new Error('Failed to accept request');
  return res.json();
}

export async function declineFriendRequest(senderId) {
  const res = await fetch(`${baseUrl()}/friends/decline`, {
    method: 'POST', headers: await authHeaders(),
    body: JSON.stringify({ senderId }),
  });
  if (!res.ok) throw new Error('Failed to decline request');
  return res.json();
}

export async function removeFriend(friendId) {
  const res = await fetch(`${baseUrl()}/friends/${encodeURIComponent(friendId)}`, {
    method: 'DELETE', headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to remove friend');
  if (res.status === 204) return null;
  return res.json().catch(() => null);
}

// Match invite flow. Now delivered via WebSocket. The new server requires a
// deckId on send/accept; call sites in app.jsx will be updated in the
// matchmaking-flow stage to surface a deck picker before dispatching these.
// `automations` is explicit and defaults to manual. The invite flow has no
// setup dialog, and manual tabletop play is the session default; an automated
// friend match goes through the Play screen's instance flow instead. The
// server treats an OMITTED field as automated (pre-automation-mode clients).
export function sendMatchInvite(targetId, deckId, automations = false) {
  return send('invite:send', { targetId, deckId, automations });
}

export function acceptMatchInvite(senderId, deckId) {
  return send('invite:accept', { senderId, deckId });
}

export function declineMatchInvite(senderId) {
  return send('invite:decline', { senderId });
}

// Spectate and trade endpoints are not implemented on the new server yet.
// Their call sites have been replaced with no-op stubs so the UI keeps
// compiling; they will be reintroduced once the server-side protocol exists.

export async function getPublicProfile(profileId) {
  const res = await fetch(`${baseUrl()}/profile/${profileId}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load profile');
  return res.json();
}
